<template>
  <div class="MediaBankPage w-full bg-lighter min-h-screen">

    <div class="layout-container flex flex-col desk:flex-row items-start desk:items-center gap-16 desk:gap-24 py-24 desk:border-b border-border-subtle">

      <!-- Title -->
      <div class="w-[240px] shrink-0">
        <h1 class="type-headline-lg leading-single">
          {{ $t('mediaBank.title') }}
        </h1>
      </div>

      <div class="w-full flex gap-8 desk:gap-16 flex-1 min-w-0">
        <!-- Search -->
        <div class="flex-1 min-w-0">
          <div class="flex-1 relative">
            <img
              src="/icons/search.svg"
              class="absolute left-12 top-1/2 -translate-y-1/2 w-16 h-16 pointer-events-none"
              alt=""
            >
            <input
              type="text"
              class="w-full border border-light pl-40 pr-12 h-40 type-sm bg-lightest"
              :placeholder="$t('mediaBank.search.placeholder')"
              :value="searchQuery"
              @input="onSearchInput"
            >
          </div>
        </div>

        <!-- Selected -->
        <button
          class="flex items-center gap-8 shrink-0 btn"
          :class="{ 'btn--secondary': mediaBankStore.favoriteCount < 1 }"
          @click="showSaved = true"
        >
          <img
            :src="mediaBankStore.favoriteCount > 0 ? '/icons/check-inv.svg' : '/icons/plus.svg'"
            class="w-16 h-16"
            alt=""
          >
          {{ $t('mediaBank.selected.button') }}
          <span v-if="mediaBankStore.favoriteCount > 0">
            ({{ mediaBankStore.favoriteCount }})
          </span>
        </button>

        <!-- Download netto assortment -->
        <button
          v-if="showDownloadNetto"
          class="flex items-center justify-center gap-8 shrink-0 btn btn--secondary"
          :disabled="downloadingZip || downloadingAllZip"
          @click="downloadAllAssortment"
        >
          <LoadingIcon v-if="downloadingAllZip" class="flex" />
          <img
            v-else
            src="/icons/download.svg"
            class="w-16 h-16"
            alt=""
          >
          {{ downloadingAllZip ? (downloadBatchLabel || $t('mediaBank.selected.downloading')) : $t('mediaBank.downloadNettoAssortment') }}
        </button>
      </div>

      <button
        class="w-full shrink-0 btn desk:hidden flex items-center gap-8"
        @click="showFilterMobile = true"
      >
        <img
          src="/icons/filter-inv.svg"
          class="w-16 h-16"
          alt=""
        >
        {{ $t('mediaBank.filters.button') }}
        <span
          v-if="hasActiveFilters"
          class="w-20 h-20 bg-darkest text-lightest flex items-center justify-center type-xs"
        >
          {{ activeFilterCount }}
        </span>
      </button>
    </div>

    <!-- Login banner for non-logged-in B2B users (mobile only) -->
    <div
      v-if="showLoginBanner"
      class="layout-container border-b border-border-subtle pb-16 desk:hidden"
    >
      <div class="bg-lightest border border-border-subtle p-16 flex flex-col items-center gap-12 text-center">
        <p class="type-sm">
          {{ $t('mediaBank.loginBanner.description') }}
        </p>
        <button
          class="btn btn--sm w-full"
          @click="showLoginModal = true"
        >
          {{ $t('signin.signin') }}
        </button>
      </div>
    </div>

    <!-- Main content: sidebar + grid -->
    <div class="layout-container flex gap-24 py-24">

      <!-- Sidebar: desktop always visible -->
      <div class="hidden desk:block w-[240px] shrink-0">
        <div class="type-headline-sm mb-16 flex items-center justify-between">
          <span>{{ $t('mediaBank.filters.title') }}</span>
          <button
            v-if="hasActiveFilters"
            class="btn btn--text type-xs"
            @click="resetFilters"
          >
            {{ $t('filter.button.clear') }}
          </button>
        </div>
        <MediaBankFilters
          :filter-groups="filterGroups"
          :total-results="filteredAssets.length"
          :active-filters="activeFilters"
          @set-filter="setFilter"
          @reset="resetFilters"
          @close-filter="showFilterMobile = false"
        />

        <!-- Login banner for non-logged-in B2B users (desktop) -->
        <div
          v-if="showLoginBanner"
          class="bg-lightest border border-border-subtle p-16 mt-24"
        >
          <p class="type-sm mb-12">
            {{ $t('mediaBank.loginBanner.description') }}
          </p>
          <button
            class="btn btn--sm w-full"
            @click="showLoginModal = true"
          >
            {{ $t('signin.signin') }}
          </button>
        </div>
      </div>

      <!-- Mobile filter slide-in -->
      <transition name="fromLeft">
        <MediaBankFilters
          v-if="showFilterMobile"
          class="desk:hidden"
          :filter-groups="filterGroups"
          :total-results="filteredAssets.length"
          :active-filters="activeFilters"
          @set-filter="setFilter"
          @reset="resetFilters"
          @close-filter="showFilterMobile = false"
        />
      </transition>

      <!-- Asset grid -->
      <div class="flex-1 min-w-0">
        <!-- Loading -->
        <div
          v-if="loading"
          class="flex justify-center items-center py-96"
        >
          <LoadingIcon />
        </div>

        <!-- No results -->
        <div
          v-else-if="filteredAssets.length === 0"
          class="w-full min-h-240 px-24 py-96 border border-dashed border-border-base flex flex-col justify-center items-center gap-2 text-center"
        >
          <img
            src="/icons/search-remove.svg"
            class="w-40 h-40 opacity-50 mb-16"
            alt=""
          >
          <p class="type-headline-sm">
            {{ $t('mediaBank.noResults') }}
          </p>
          <button
            v-if="hasActiveFilters"
            class="btn btn--secondary mt-16"
            @click="resetFilters"
          >
            {{ $t('filter.button.clear') }}
          </button>
        </div>

        <!-- Results -->
        <div v-else>
          <!-- Results header with count and view toggle -->
          <div class="flex justify-between items-center mb-16">
            <div class="type-headline-sm">{{ $t('mediaBank.results', { num: filteredAssets.length }) }}</div>
            <div class="flex gap-8">
              <button
                class="w-24 h-24 hover:opacity-100 transition-all"
                :class="viewMode === 'grid' ? 'opacity-100' : 'opacity-50'"
                @click="viewMode = 'grid'"
              >
                <img
                  src="/icons/grid.svg"
                  class="w-24 h-24"
                  alt="grid"
                >
              </button>
              <button
                class="w-24 h-24 hover:opacity-100 transition-all"
                :class="viewMode === 'list' ? 'opacity-100' : 'opacity-50'"
                @click="viewMode = 'list'"
              >
                <img
                  src="/icons/list.svg"
                  class="w-24 h-24"
                  alt="list"
                >
              </button>
            </div>
          </div>

          <!-- Grid view -->
          <div
            v-if="viewMode === 'grid'"
            class="grid grid-cols-2 desk:grid-cols-3 deskStd:grid-cols-4 gap-8 desk:gap-16"
          >
            <MediaBankCard
              v-for="asset in paginatedAssets"
              :key="asset.id"
              :asset="asset"
              @preview="openPreview"
              @download="handleDownload"
              @toggle-favorite="mediaBankStore.toggleFavorite($event)"
            />
          </div>

          <!-- List view -->
          <div v-else>
            <MediaBankCardList
              v-for="asset in paginatedAssets"
              :key="asset.id"
              :asset="asset"
              @preview="openPreview"
              @download="handleDownload"
              @toggle-favorite="mediaBankStore.toggleFavorite($event)"
            />
          </div>
        </div>

        <!-- Load more -->
        <div
          v-if="filteredAssets.length > page * pageSize"
          class="flex justify-center mt-32"
        >
          <button
            class="btn btn--secondary"
            @click="page++"
          >
            {{ $t('mediaBank.loadMore') }}
            ({{ filteredAssets.length - page * pageSize }} {{ $t('mediaBank.loadMore.remaining') }})
          </button>
        </div>
      </div>
    </div>

    <!-- Selected side menu -->
    <transition name="fromRight">
      <GenericSideMenu
        v-if="showSaved"
        :title="$t('mediaBank.selected.title')"
        :title-meta="mediaBankStore.favoriteCount > 0 ? mediaBankStore.favoriteCount : null"
        position="right"
        :self-contained="true"
        @close="!showTermsModal && (showSaved = false)"
      >
        <!-- Empty state -->
        <div
          v-if="mediaBankStore.favorites.length === 0"
          class="flex flex-col items-center justify-center py-96 text-text-subtle"
        >
          <img
            src="/icons/plus.svg"
            class="w-48 h-48 opacity-30 mb-16"
            alt=""
          >
          <p class="type-sm">
            {{ $t('mediaBank.selected.empty') }}
          </p>
        </div>

        <!-- Grid -->
        <div
          v-else
          class="grid grid-cols-2 gap-8"
        >
          <MediaBankCard
            v-for="asset in mediaBankStore.favorites"
            :key="asset.id"
            :asset="asset"
            @preview="openPreview"
            @download="handleDownload"
            @toggle-favorite="mediaBankStore.toggleFavorite($event)"
          />
        </div>

        <template #bottom>
          <div v-if="mediaBankStore.favoriteCount > 0" class="p-16 flex flex-col gap-8">
            <button
              class="btn btn--lg w-full flex items-center justify-center gap-8"
              :disabled="downloadingZip || downloadingAllZip"
              @click.stop="downloadAllSaved"
            >
              <LoadingIcon v-if="downloadingZip" class="flex invert" />
              {{ downloadingZip ? (downloadBatchLabel || $t('mediaBank.selected.downloading')) : $t('mediaBank.selected.downloadAll') }}
            </button>
            <button
              class="btn btn--lg btn--secondary w-full flex items-center justify-center gap-8"
              @click.stop="exportToExcel"
            >
              {{ $t('mediaBank.selected.exportExcel') }}
            </button>
            <button
              class="btn btn--text w-full flex items-center justify-center gap-8"
              :disabled="downloadingZip || downloadingAllZip"
              @click.stop="onClearClick"
            >
              <img
                src="/icons/trash.svg"
                class="w-16 h-16"
                alt=""
              >
              {{ confirmingClear ? $t('mediaBank.selected.clearConfirm') : $t('mediaBank.filter.clear') }}
            </button>
          </div>
        </template>
      </GenericSideMenu>
    </transition>

    <!-- Preview modal -->
    <MediaBankPreviewModal
      v-if="previewAsset"
      :asset="previewAsset"
      :downloading-url="downloadingFileUrl"
      @close="previewAsset = null"
      @download="handleDownload"
      @toggle-favorite="mediaBankStore.toggleFavorite($event)"
    />

    <!-- Terms modal -->
    <MediaBankTermsModal
      v-if="showTermsModal"
      @accept="onTermsAccepted"
      @cancel="showTermsModal = false"
    />

    <!-- Login modal for B2B users -->
    <transition name="fromRight">
      <GenericSideMenu
        v-if="showLoginModal"
        :title="$t('signin.title')"
        position="right"
        :self-contained="true"
        @close="showLoginModal = false"
      >
        <div class="p-16">
          <SigninModal :as-modal="false" :stay-on-page="true" />
        </div>
      </GenericSideMenu>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { useMediaBankStore } from '~/store/mediaBank';
import { useGlobalContentStore } from '~/store/globalContent';
import { useUiStore } from '~/store/ui';
import { useUserStore } from '~/store/user';
import useMediaBank from '~/composables/useMediaBank';
import MediaBankCard from '~/components/media-bank/MediaBankCard.vue';
import MediaBankCardList from '~/components/media-bank/MediaBankCardList.vue';
import MediaBankFilters from '~/components/media-bank/MediaBankFilters.vue';
import MediaBankPreviewModal from '~/components/media-bank/MediaBankPreviewModal.vue';
import MediaBankTermsModal from '~/components/media-bank/MediaBankTermsModal.vue';
import LoadingIcon from '~/components/generic/LoadingIcon.vue';
import SigninModal from '~/components/account/SigninModal.vue';
import type { MediaAsset } from '#constants/types/mediaBank';
import GenericSideMenu from '~/components/menu/GenericSideMenu.vue';
import useZipDownload from '~/composables/useZipDownload';

const props = withDefaults(defineProps<{
  mode?: 'b2b' | 'b2c';
  isLoggedIn?: boolean;
}>(), {
  mode: 'b2b',
  isLoggedIn: true, // Default true for B2C (no login concept)
});

const { $t } = useNuxtApp();
const mediaBankStore = useMediaBankStore();
const uiStore = useUiStore();
const userStore = useUserStore();
const globalContentStore = useGlobalContentStore();
const route = useRoute();

globalContentStore.setIsB2b(props.mode === 'b2b');

// Helper to trigger browser download from blob
const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Show documents only for B2C or B2B when not logged in
const showDocumentsOnly = computed(() => props.mode === 'b2c' || (props.mode === 'b2b' && !props.isLoggedIn));

// Only show the netto download button for logged-in B2B users who have a fixed pricelist
const showDownloadNetto = computed(() =>
  props.mode === 'b2b' && props.isLoggedIn && (userStore.userProfile?.fixedPricelistIds ?? []).length > 0
);

// Show login banner for B2B users who are not logged in
const showLoginBanner = computed(() => props.mode === 'b2b' && !props.isLoggedIn);

const mediaBankOptions = computed(() => ({
  documentsOnly: showDocumentsOnly.value,
  excludeProductTypes: props.mode === 'b2c' ? ['Resilient'] : [],
}));

const {
  filteredAssets,
  filterGroups,
  loading,
  searchQuery,
  activeFilters,
  hasActiveFilters,
  loadAssets,
  setFilter,
  resetFilters,
  fetchFixedPricelistAssets,
} = useMediaBank(mediaBankOptions);

// Load default assets on server + client if no search query in URL
const q = route.query.q as string | undefined;
const filters = route.query.filters as string | undefined;
const viewFromUrl = route.query.view as string | undefined;

if (q) {
  searchQuery.value = q;
}
if (filters) {
  try {
    const parsed = JSON.parse(decodeURIComponent(filters));
    Object.keys(parsed).forEach((key) => {
      setFilter(key as keyof typeof activeFilters.value, parsed[key]);
    });
  } catch (e) {
    console.warn('Failed to parse filters from URL', e);
  }
}

// Client-only initialization and asset loading
onMounted(async() => {
  mediaBankStore.initFromStorage(props.mode);
  await loadAssets();
});

// View mode (defined early as updateUrl references it)
const viewMode = useCookie<'grid' | 'list'>('mediaBankViewMode', {
  maxAge: 3600 * 24 * 182,
  path: '/',
  default: () => props.mode === 'b2c' ? 'list' : 'grid',
});

// Apply view mode from URL if provided
if (viewFromUrl === 'list' || viewFromUrl === 'grid') {
  viewMode.value = viewFromUrl;
}

// Update URL when search/filters change
const updateUrl = () => {
  const params = new URLSearchParams();
  if (searchQuery.value) {
    params.set('q', searchQuery.value);
  }
  if (hasActiveFilters.value) {
    // Only include filters that have values
    const nonEmptyFilters = Object.fromEntries(
      Object.entries(activeFilters.value).filter(([, arr]) => arr.length > 0)
    );
    params.set('filters', JSON.stringify(nonEmptyFilters));
  }
  if (viewMode.value) {
    params.set('view', viewMode.value);
  }
  const newUrl = params.toString() ? `${route.path}?${params.toString()}` : route.path;
  history.replaceState({}, '', newUrl);
};

watch([searchQuery, activeFilters, viewMode], () => {
  updateUrl();
}, { deep: true });

// Search input handler
const onSearchInput = (event: Event) => {
  const val = (event.target as HTMLInputElement).value;
  searchQuery.value = val;
};

// Pagination
const page = ref(1);
const pageSize = 48;
const paginatedAssets = computed(() => filteredAssets.value.slice(0, page.value * pageSize));

// Reset page when filters/search change
watch([filteredAssets], () => {
  page.value = 1;
});

// UI state
const showSaved = ref(false);
const showFilterMobile = ref(false);
const previewAsset = ref<MediaAsset | null>(null);
const showTermsModal = ref(false);
const showLoginModal = ref(false);
const pendingDownloadAsset = ref<MediaAsset | null>(null);
const pendingDownloadAll = ref(false);

// Close login modal when user logs in
watch(() => props.isLoggedIn, (loggedIn) => {
  if (loggedIn) {
    showLoginModal.value = false;
  }
});

const activeFilterCount = computed(() =>
  Object.values(activeFilters.value).reduce((sum, arr) => sum + arr.length, 0)
);

// Two-step clear: first click asks for confirmation, second click clears
const confirmingClear = ref(false);
let clearConfirmTimer: ReturnType<typeof setTimeout> | undefined;

const onClearClick = () => {
  clearTimeout(clearConfirmTimer);
  if (confirmingClear.value) {
    confirmingClear.value = false;
    mediaBankStore.clearFavorites();
    return;
  }
  confirmingClear.value = true;
  clearConfirmTimer = setTimeout(() => {
    confirmingClear.value = false;
  }, 4000);
};

onBeforeUnmount(() => clearTimeout(clearConfirmTimer));

const openPreview = (asset: MediaAsset) => {
  previewAsset.value = asset;
};

// Helper to build filename with correct extension
const buildFilename = (asset: MediaAsset, isTiff = false): string => {
  const baseName = (asset.name || 'download').replace(/\.[^.]+$/, ''); // Remove existing extension

  if (asset.mediaType === 'Images') {
    return `${baseName}${isTiff ? '.tiff' : '.jpg'}`;
  }
  // Documents - ensure extension
  const ext = asset.fileType?.startsWith('.') ? asset.fileType : `.${asset.fileType || 'pdf'}`;
  return `${baseName}${ext}`;
};

const triggerDownload = async(asset: MediaAsset, url?: string) => {
  const downloadUrl = url || asset.url;
  const isTiff = Boolean(asset.originalTiffUrl && downloadUrl === asset.originalTiffUrl);
  const mimeType = isTiff ? 'image/tiff' : asset.mimeType;
  const filename = buildFilename(asset, isTiff);

  const proxyUrl = `/filedownload/?origin=${encodeURIComponent(downloadUrl)}&mimeType=${encodeURIComponent(mimeType)}`;

  try {
    const response = await fetch(proxyUrl);
    if (!response.ok) {
      throw new Error('Download failed');
    }
    const blob = await response.blob();
    downloadBlob(blob, filename);
  } catch (error) {
    console.error('Download failed:', error);
    uiStore.setTemporaryError($t('mediaBank.error.downloadFailed'), 5000);
  }
};

const pendingDownloadUrl = ref<string | undefined>(undefined);
const downloadingFileUrl = ref<string | null>(null);

const handleDownload = async(asset: MediaAsset, url?: string) => {
  if (!mediaBankStore.termsAccepted) {
    pendingDownloadAsset.value = asset;
    pendingDownloadUrl.value = url;
    showTermsModal.value = true;
    return;
  }
  // Set loading state for this specific URL
  const downloadUrl = url || asset.url;
  downloadingFileUrl.value = downloadUrl;

  await triggerDownload(asset, url);

  // Clear loading when download completes
  downloadingFileUrl.value = null;
};

const ZIP_BATCH_SIZE = 150;

const zipDownload = useZipDownload();

const buildZipHeaders = () => ({
  'Content-Type': 'application/json',
  'HostName': globalContentStore.hostname ?? '',
  'BasePath': globalContentStore.getApiApplicationBasePath ?? '',
  'ApplicationId': `${globalContentStore.getApplicationId ?? ''}`,
  'IsB2B': globalContentStore.getIsB2b ? 'true' : 'false',
});

const batchFiles = (files: { url: string; filename: string }[], batchSize: number): { url: string; filename: string }[][] => {
  const batches: { url: string; filename: string }[][] = [];
  for (let i = 0; i < files.length; i += batchSize) {
    batches.push(files.slice(i, i + batchSize));
  }
  return batches;
};

const downloadingZip = ref(false);
const downloadingAllZip = ref(false);
const downloadBatchLabel = ref('');
const pendingDownloadAllAssortment = ref(false);

const doDownloadAllAssortment = async() => {
  downloadingAllZip.value = true;
  downloadBatchLabel.value = '';
  try {
    const assortmentAssets = await fetchFixedPricelistAssets();

    if (!assortmentAssets.length) {
      console.warn('No netto assortment assets found');
      uiStore.setTemporaryError($t('mediaBank.error.downloadFailed'), 5000);
      return;
    }

    const files: { url: string; filename: string }[] = [];
    assortmentAssets.forEach((asset) => {
      if (asset.mediaType === 'Images') {
        files.push({
          url: asset.originalJpgUrl || asset.url,
          filename: buildFilename(asset, false),
        });
      } else {
        files.push({
          url: asset.url,
          filename: buildFilename(asset),
        });
      }
    });

    const batches = batchFiles(files, ZIP_BATCH_SIZE);
    const zipHeaders = buildZipHeaders();

    for (let i = 0; i < batches.length; i++) {
      downloadBatchLabel.value = batches.length > 1
        ? $t('mediaBank.downloadNettoAssortment.progress', { current: i + 1, total: batches.length })
        : '';
      const { blob } = await zipDownload.downloadZipBatch(batches[i], zipHeaders);
      const suffix = batches.length > 1 ? `-part${i + 1}` : '';
      downloadBlob(blob, `netto-assortment${suffix}.zip`);
    }
  } catch (error) {
    console.error('Failed to download netto assortment ZIP:', error);
    uiStore.setTemporaryError($t('mediaBank.error.downloadFailed'), 5000);
  } finally {
    downloadingAllZip.value = false;
    downloadBatchLabel.value = '';
  }
};

const downloadAllAssortment = () => {
  if (!mediaBankStore.termsAccepted) {
    pendingDownloadAllAssortment.value = true;
    showTermsModal.value = true;
    return;
  }
  doDownloadAllAssortment();
};

const doDownloadAllSaved = async() => {
  const files: { url: string; filename: string }[] = [];

  mediaBankStore.favorites.forEach((asset) => {
    if (asset.mediaType === 'Images') {
      // Add JPG
      files.push({
        url: asset.originalJpgUrl || asset.url,
        filename: buildFilename(asset, false),
      });
      // Add TIFF if available
      if (asset.originalTiffUrl) {
        files.push({
          url: asset.originalTiffUrl,
          filename: buildFilename(asset, true),
        });
      }
    } else {
      // Document
      files.push({
        url: asset.url,
        filename: buildFilename(asset),
      });
    }
  });

  try {
    downloadingZip.value = true;

    const batches = batchFiles(files, ZIP_BATCH_SIZE);
    const zipHeaders = buildZipHeaders();

    for (let i = 0; i < batches.length; i++) {
      downloadBatchLabel.value = batches.length > 1
        ? $t('mediaBank.downloadNettoAssortment.progress', { current: i + 1, total: batches.length })
        : '';
      const { blob } = await zipDownload.downloadZipBatch(batches[i], zipHeaders);
      const suffix = batches.length > 1 ? `-part${i + 1}` : '';
      downloadBlob(blob, `media-bank-download${suffix}.zip`);
    }
  } catch (error) {
    console.error('Failed to download ZIP:', error);
    uiStore.setTemporaryError($t('mediaBank.error.downloadFailed'), 5000);
  } finally {
    downloadingZip.value = false;
    downloadBatchLabel.value = '';
  }
};

const downloadAllSaved = () => {
  if (!mediaBankStore.termsAccepted) {
    pendingDownloadAll.value = true;
    showTermsModal.value = true;
    return;
  }
  doDownloadAllSaved();
};

const exportToExcel = () => {
  const headers = ['Product Name', 'Article Number', 'Filename', 'Type', 'URL', 'TIFF URL'];
  const rows = mediaBankStore.favorites.map((asset) => [
    asset.productName,
    asset.articleNumber,
    asset.name || '',
    asset.mediaSetTitle,
    asset.mediaType === 'Images' ? (asset.originalJpgUrl || asset.url) : asset.url,
    asset.mediaType === 'Images' ? (asset.originalTiffUrl || '') : '',
  ]);

  const csvContent = [
    headers.join(';'),
    ...rows.map((row) => row.map((cell) => `"${(cell || '').replace(/"/g, '""')}"`).join(';')),
  ].join('\n');

  const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
  downloadBlob(blob, 'media-bank-selection.csv');
};

const onTermsAccepted = async() => {
  mediaBankStore.acceptTerms();
  showTermsModal.value = false;

  if (pendingDownloadAllAssortment.value) {
    pendingDownloadAllAssortment.value = false;
    await doDownloadAllAssortment();
  } else if (pendingDownloadAll.value) {
    pendingDownloadAll.value = false;
    await doDownloadAllSaved();
  } else if (pendingDownloadAsset.value) {
    const asset = pendingDownloadAsset.value;
    const url = pendingDownloadUrl.value;
    pendingDownloadAsset.value = null;
    pendingDownloadUrl.value = undefined;
    await handleDownload(asset, url);
  }
};
</script>
